/**
 * Keka-style timesheet grid: fixed category rows × day columns of editable hour cells.
 *
 * Two row groups — "Active Hours" and "Time Off" — render in a deterministic
 * order (matching the backend's canonical category list). Per-cell upsert
 * fires on blur via the `onCellChange` callback.
 */

import { useEffect, useMemo, useState, type CSSProperties } from "react";

import type { TimesheetCategoryGroup, TimesheetCategoryRow } from "@/lib/types";

type Props = {
  from: Date;
  to: Date;
  rows: TimesheetCategoryRow[];
  totalsByDay: Record<string, number>;
  loading: boolean;
  onCellChange: (category: string, workDate: string, hours: number) => void;
  // When true, every cell renders as plain text instead of an input. Used
  // when the week has been submitted or approved (lock state).
  readOnly?: boolean;
};

const WEEKEND_BG = "rgba(0,0,0,0.025)";
const TODAY_BG = "rgba(122,61,255,0.06)";
const HEADER_BG = "var(--surface-1)";
const GROUP_BG = "var(--surface-2)";

const GROUP_LABEL: Record<TimesheetCategoryGroup, string> = {
  active: "Active Hours",
  time_off: "Time Off",
};

function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function eachDay(from: Date, to: Date): Date[] {
  const days: Date[] = [];
  const d = new Date(from);
  while (d <= to) {
    days.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function hoursFromSeconds(seconds: number): string {
  if (!seconds) return "";
  return (seconds / 3600).toFixed(2);
}

function totalHours(seconds: number): string {
  if (!seconds) return "0";
  const h = seconds / 3600;
  return h.toFixed(h % 1 === 0 ? 0 : 2);
}

export function TimesheetGridView({
  from,
  to,
  rows,
  totalsByDay,
  loading,
  onCellChange,
  readOnly = false,
}: Props) {
  const days = useMemo(() => eachDay(from, to), [from, to]);
  const todayISO = fmtISO(new Date());

  // Group by category group, preserving the order rows arrived in (matches
  // the canonical ordering from the backend).
  const groups = useMemo(() => {
    const seen = new Map<TimesheetCategoryGroup, TimesheetCategoryRow[]>();
    for (const row of rows) {
      const arr = seen.get(row.group) ?? [];
      arr.push(row);
      seen.set(row.group, arr);
    }
    return Array.from(seen.entries());
  }, [rows]);

  const groupTotals = useMemo(() => {
    const out = new Map<TimesheetCategoryGroup, Record<string, number>>();
    for (const [group, gRows] of groups) {
      const t: Record<string, number> = {};
      for (const row of gRows) {
        for (const [day, sec] of Object.entries(row.by_day)) {
          t[day] = (t[day] ?? 0) + sec;
        }
      }
      out.set(group, t);
    }
    return out;
  }, [groups]);

  const grandTotalAll = Object.values(totalsByDay).reduce((a, b) => a + b, 0);

  return (
    <div
      style={{
        border: "1px solid var(--hairline)",
        borderRadius: 4,
        overflow: "auto",
        background: "var(--canvas)",
      }}
    >
      <table
        style={{
          width: "max-content",
          minWidth: "100%",
          borderCollapse: "collapse",
          fontSize: 13,
        }}
      >
        <thead>
          <tr style={{ background: HEADER_BG }}>
            <th style={{ ...stickyLeft, ...headerCell, width: 220, minWidth: 220, zIndex: 3 }}>
              Category
            </th>
            <th style={{ ...headerCell, width: 70, minWidth: 70, textAlign: "center" }}>
              Total
            </th>
            {days.map((d) => {
              const iso = fmtISO(d);
              const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const isToday = iso === todayISO;
              return (
                <th
                  key={iso}
                  style={{
                    ...headerCell,
                    width: 60,
                    minWidth: 60,
                    textAlign: "center",
                    background: isToday ? TODAY_BG : isWeekend ? WEEKEND_BG : undefined,
                  }}
                  title={d.toDateString()}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "var(--ink)",
                    }}
                  >
                    {d.getDate()}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "var(--mute)",
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                    }}
                  >
                    {weekday}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={days.length + 2}
                style={{ padding: 20, textAlign: "center", color: "var(--mute)" }}
              >
                Loading…
              </td>
            </tr>
          )}

          {!loading &&
            groups.map(([group, gRows]) => (
              <CategoryGroup
                key={group}
                group={group}
                rows={gRows}
                days={days}
                todayISO={todayISO}
                groupTotals={groupTotals.get(group) ?? {}}
                onCellChange={onCellChange}
                readOnly={readOnly}
              />
            ))}
        </tbody>

        {!loading && (
          <tfoot>
            <tr style={{ background: HEADER_BG, fontWeight: 500 }}>
              <td
                style={{ ...stickyLeft, ...cellBase, fontSize: 12, paddingLeft: 12 }}
              >
                Total
              </td>
              <td
                style={{
                  ...cellBase,
                  textAlign: "center",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {totalHours(grandTotalAll)}
              </td>
              {days.map((d) => {
                const iso = fmtISO(d);
                const v = totalsByDay[iso] ?? 0;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = iso === todayISO;
                return (
                  <td
                    key={iso}
                    style={{
                      ...cellBase,
                      textAlign: "center",
                      background: isToday ? TODAY_BG : isWeekend ? WEEKEND_BG : undefined,
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      color: v ? "var(--ink)" : "var(--mute)",
                    }}
                  >
                    {totalHours(v)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function CategoryGroup({
  group,
  rows,
  days,
  todayISO,
  groupTotals,
  onCellChange,
  readOnly,
}: {
  group: TimesheetCategoryGroup;
  rows: TimesheetCategoryRow[];
  days: Date[];
  todayISO: string;
  groupTotals: Record<string, number>;
  onCellChange: (category: string, workDate: string, hours: number) => void;
  readOnly: boolean;
}) {
  const groupGrand = Object.values(groupTotals).reduce((a, b) => a + b, 0);
  return (
    <>
      <tr style={{ background: GROUP_BG }}>
        <td
          style={{
            ...stickyLeft,
            ...cellBase,
            padding: "6px 12px",
            fontWeight: 600,
            background: GROUP_BG,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "var(--body-mid)",
              letterSpacing: 0.6,
              textTransform: "uppercase",
            }}
          >
            {GROUP_LABEL[group]}
          </span>
        </td>
        <td
          style={{
            ...cellBase,
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {totalHours(groupGrand)}
        </td>
        {days.map((d) => {
          const iso = fmtISO(d);
          const v = groupTotals[iso] ?? 0;
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          const isToday = iso === todayISO;
          return (
            <td
              key={iso}
              style={{
                ...cellBase,
                textAlign: "center",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                fontWeight: 500,
                background: isToday ? TODAY_BG : isWeekend ? WEEKEND_BG : undefined,
                color: v ? "var(--ink)" : "var(--mute)",
              }}
            >
              {totalHours(v)}
            </td>
          );
        })}
      </tr>

      {rows.map((row) => (
        <CategoryRow
          key={row.code}
          row={row}
          days={days}
          todayISO={todayISO}
          onCellChange={onCellChange}
          readOnly={readOnly}
        />
      ))}
    </>
  );
}

function CategoryRow({
  row,
  days,
  todayISO,
  onCellChange,
  readOnly,
}: {
  row: TimesheetCategoryRow;
  days: Date[];
  todayISO: string;
  onCellChange: (category: string, workDate: string, hours: number) => void;
  readOnly: boolean;
}) {
  const rowTotal = row.total_seconds;
  return (
    <tr>
      <td style={{ ...stickyLeft, ...cellBase, padding: "6px 12px" }}>
        <span style={{ fontSize: 13, color: "var(--ink)" }}>{row.label}</span>
      </td>
      <td
        style={{
          ...cellBase,
          textAlign: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          fontWeight: 500,
          color: rowTotal ? "var(--ink)" : "var(--mute)",
        }}
      >
        {totalHours(rowTotal)}
      </td>
      {days.map((d) => {
        const iso = fmtISO(d);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isToday = iso === todayISO;
        return (
          <td
            key={iso}
            style={{
              ...cellBase,
              padding: 0,
              background: isToday ? TODAY_BG : isWeekend ? WEEKEND_BG : undefined,
            }}
          >
            <CellInput
              category={row.code}
              workDate={iso}
              initialSeconds={row.by_day[iso] ?? 0}
              onChange={onCellChange}
              readOnly={readOnly}
            />
          </td>
        );
      })}
    </tr>
  );
}

function CellInput({
  category,
  workDate,
  initialSeconds,
  onChange,
  readOnly,
}: {
  category: string;
  workDate: string;
  initialSeconds: number;
  onChange: (category: string, workDate: string, hours: number) => void;
  readOnly: boolean;
}) {
  const initial = hoursFromSeconds(initialSeconds);
  const [value, setValue] = useState(initial);

  useEffect(() => setValue(initial), [initial]);

  if (readOnly) {
    return (
      <div
        style={{
          width: "100%",
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: initialSeconds ? "var(--ink)" : "var(--mute-soft)",
        }}
      >
        {initialSeconds ? hoursFromSeconds(initialSeconds) : "—"}
      </div>
    );
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const cleaned = value.trim();
        const parsed = cleaned === "" ? 0 : Number(cleaned);
        if (!Number.isFinite(parsed) || parsed < 0 || parsed > 24) {
          setValue(initial);
          return;
        }
        const rounded = Math.round(parsed * 100) / 100;
        const currentHours = initialSeconds / 3600;
        if (Math.abs(rounded - currentHours) < 0.005) {
          setValue(rounded ? rounded.toFixed(2) : "");
          return;
        }
        onChange(category, workDate, rounded);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      style={{
        width: "100%",
        height: 28,
        background: "transparent",
        border: "none",
        borderLeft: "1px solid var(--hairline)",
        textAlign: "center",
        fontFamily: "var(--font-mono)",
        fontSize: 12,
        color: value ? "var(--ink)" : "var(--mute)",
        outline: "none",
        padding: 0,
      }}
    />
  );
}

const stickyLeft: CSSProperties = {
  position: "sticky",
  left: 0,
  background: "var(--canvas)",
  zIndex: 2,
  borderRight: "1px solid var(--hairline)",
};

const headerCell: CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--hairline)",
  position: "sticky",
  top: 0,
  fontWeight: 500,
  fontSize: 11,
  color: "var(--body-mid)",
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "left",
  background: HEADER_BG,
  zIndex: 2,
};

const cellBase: CSSProperties = {
  borderBottom: "1px solid var(--hairline)",
  height: 32,
  padding: "0 6px",
};
